import { useMemo, useState } from "react";
import type { Contact } from "@veto/domain";
import { GROUP_MAX_MEMBERS_V1 } from "@veto/protocol";
import { ModalShell } from "./ModalShell";

/** Creator is always included → at most MAX-1 selectable contacts (FR-GRP-001). */
const MAX_SELECTABLE = GROUP_MAX_MEMBERS_V1 - 1;

export function CreateGroupModal({
  contacts,
  onClose,
  onCreate,
}: {
  contacts: Contact[];
  onClose: () => void;
  onCreate: (title: string, memberIds: string[]) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const list = useMemo(() => contacts.filter((c) => !c.blockedAt), [contacts]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SELECTABLE) next.add(id);
      return next;
    });
  }

  return (
    <ModalShell
      title="New group"
      ariaLabel="Create group"
      onClose={onClose}
      size="md"
      className="add-contact-card"
      footer={
        <div className="actions-row">
          <button type="button" className="btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || selected.size < 1 || !title.trim()}
            onClick={() => {
              setBusy(true);
              void onCreate(title.trim(), [...selected]).finally(() => setBusy(false));
            }}
          >
            {busy ? "Creating…" : "Create group"}
          </button>
        </div>
      }
    >
      <div className="stack">
        <p className="muted">
          Up to {GROUP_MAX_MEMBERS_V1} members (including you). New members don&apos;t see past
          messages.
        </p>
        <div className="field">
          <label htmlFor="groupTitle">Group name</label>
          <input id="groupTitle" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="scroll-list">
          {list.length === 0 && <p className="muted">Add contacts before creating a group.</p>}
          {list.map((c) => {
            const checked = selected.has(c.id);
            const atCap = !checked && selected.size >= MAX_SELECTABLE;
            return (
              <label key={c.id} className="check">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={atCap}
                  onChange={() => toggle(c.id)}
                />
                <span>
                  <strong>{c.displayName}</strong>
                  <span className="muted"> · {c.verificationState}</span>
                </span>
              </label>
            );
          })}
        </div>
        <p className="muted text-xs" aria-live="polite">
          Selected {selected.size}/{MAX_SELECTABLE}
        </p>
      </div>
    </ModalShell>
  );
}
